import { useTranslation } from '@numistic/i18n'
import { cn } from '@numistic/ui'
import { useMemo, useState, type ReactNode } from 'react'

export interface TreeNode {
  id: string
  parentId: string | null
  name: string
  count: number
  isInbox?: boolean
}

/**
 * Дерево коллекций или мест хранения (один компонент на оба вида).
 * Перетаскивание узла на другой — вложить; на «В корень» — вынести наверх.
 */
export function TreeView({
  nodes,
  selectedId,
  onSelect,
  onMove,
  actions,
  rootLabel,
}: {
  nodes: TreeNode[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  onMove?: (id: string, parentId: string | null) => void
  actions?: (node: TreeNode) => ReactNode
  rootLabel: string
}) {
  const { t } = useTranslation()
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const children = useMemo(() => {
    const map = new Map<string | null, TreeNode[]>()
    for (const n of nodes) map.set(n.parentId, [...(map.get(n.parentId) ?? []), n])
    return map
  }, [nodes])

  // Счётчик узла — вместе с вложенными коллекциями (так его и ждут: «Монеты» = всё внутри).
  const totals = useMemo(() => {
    const out = new Map<string, number>()
    const sum = (id: string): number => {
      const node = nodes.find((n) => n.id === id)
      const own = node?.count ?? 0
      const total = own + (children.get(id) ?? []).reduce((acc, c) => acc + sum(c.id), 0)
      out.set(id, total)
      return total
    }
    for (const root of children.get(null) ?? []) sum(root.id)
    return out
  }, [nodes, children])

  const drop = (target: string | null) => {
    if (dragging && dragging !== target && onMove) onMove(dragging, target)
    setDragging(null)
    setOver(null)
  }

  const render = (parentId: string | null, depth: number): ReactNode => {
    const list = children.get(parentId)
    if (!list?.length) return null
    return (
      <ul className="grid gap-0.5">
        {list.map((node) => (
          <li key={node.id}>
            <div
              draggable={Boolean(onMove) && !node.isInbox}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move'
                setDragging(node.id)
              }}
              onDragEnd={() => (setDragging(null), setOver(null))}
              onDragOver={(e) => {
                if (!dragging || dragging === node.id) return
                e.preventDefault()
                setOver(node.id)
              }}
              onDragLeave={() => setOver((o) => (o === node.id ? null : o))}
              onDrop={(e) => {
                e.preventDefault()
                drop(node.id)
              }}
              className={cn(
                'group flex items-center gap-1 rounded-md pr-1',
                node.id === selectedId && 'bg-accent',
                over === node.id && 'ring-2 ring-primary',
              )}
              style={{ paddingLeft: `${depth * 0.9 + 0.25}rem` }}
            >
              <button
                type="button"
                aria-current={node.id === selectedId || undefined}
                className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left text-sm"
                onClick={() => onSelect(node.id)}
              >
                <span className="truncate">{node.name}</span>
                {totals.get(node.id) ? (
                  <span
                    aria-hidden
                    className="text-xs tabular-nums text-muted-foreground"
                    title={t('collections.countTitle', {
                      own: node.count,
                      total: totals.get(node.id) ?? 0,
                    })}
                  >
                    {totals.get(node.id)}
                  </span>
                ) : null}
              </button>
              {actions ? (
                <span className="flex shrink-0 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100">
                  {actions(node)}
                </span>
              ) : null}
            </div>
            {render(node.id, depth + 1)}
          </li>
        ))}
      </ul>
    )
  }

  return (
    <nav className="grid gap-1" aria-label={rootLabel}>
      <button
        type="button"
        aria-current={selectedId === null || undefined}
        onClick={() => onSelect(null)}
        onDragOver={(e) => {
          if (dragging) {
            e.preventDefault()
            setOver('root')
          }
        }}
        onDragLeave={() => setOver((o) => (o === 'root' ? null : o))}
        onDrop={(e) => {
          e.preventDefault()
          drop(null)
        }}
        className={cn(
          'rounded-md px-1 py-1.5 text-left text-sm font-medium',
          selectedId === null && 'bg-accent',
          over === 'root' && 'ring-2 ring-primary',
        )}
      >
        {rootLabel}
        {dragging ? (
          <span className="ml-2 text-xs text-muted-foreground">
            ({t('collections.moveToRoot')})
          </span>
        ) : null}
      </button>
      {render(null, 0)}
    </nav>
  )
}
